import { useEffect, useRef, useState } from 'react';

import {
  multiSelectIndicatorRecipe,
  multiSelectItemLabelRecipe,
  multiSelectItemRecipe,
} from 'components/MultiSelect/MultiSelect.recipe';
import { selectItemCheckRecipe, selectItemLabelRecipe, selectItemRecipe } from 'components/Select/Select.recipe';
import { SelectCheckMark } from 'components/Select/SelectCheckMark';
import { cn } from 'components/utils';

import {
  filterInputRecipe,
  filterOptionFocusRecipe,
  filterSingleTickRecipe,
  filterTickRecipe,
} from './FilterButton.recipe';

/**
 * The tick and the dash, in Figma's own 24px frame at the 1.5px stroke `MultiSelect` draws, so a
 * filter row and a multi-select row hold the same glyph. Not imported from there: it is a private
 * `const` in that file, and this layer does not edit a component that other work is landing in.
 */
const TickIcon = () => (
  <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24">
    <path
      d="M16.2 8.85L10.32 15.15L7.8 12.45"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const DashIcon = () => (
  <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24">
    <path d="M8 12H16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
  </svg>
);

type FilterOptionRowProps = {
  mode: 'multiple' | 'single';
  /** Groups the radios of a single-select list. Unused by checkboxes. */
  name: string;
  label: string;
  checked: boolean;
  /** Select all, when some but not every row is ticked. Drawn as the dash, and announced as mixed. */
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
};

/**
 * One choice in the panel.
 *
 * A native `<label>` around a real, visually hidden `<input>`, so Tab, Space, the arrow keys of a
 * radio group and the accessible name are the platform's rather than re-implemented. The row's
 * look is `MultiSelect`'s (checkbox) or `Select`'s (radio, with a tick at the right), reused
 * unchanged. Both recipes are written against Base UI's list, which sets `data-highlighted`,
 * `data-selected` and `data-disabled` on the row; nothing here does, so this component writes them.
 *
 * `data-highlighted` follows the pointer, and keyboard focus only when it is `:focus-visible`,
 * which is when a select highlights a row from the keyboard too.
 */
export const FilterOptionRow = ({
  mode,
  name,
  label,
  checked,
  indeterminate,
  disabled,
  onChange,
}: FilterOptionRowProps) => {
  const [highlighted, setHighlighted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const multiple = mode === 'multiple';

  // `indeterminate` is a DOM property, not an attribute, so React cannot set it declaratively.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  return (
    <label
      className={cn(
        multiple ? cn('ds-checkbox', multiSelectItemRecipe()) : selectItemRecipe(),
        filterOptionFocusRecipe()
      )}
      data-disabled={disabled ? '' : undefined}
      data-highlighted={highlighted ? '' : undefined}
      data-part="option"
      data-selected={checked || indeterminate ? '' : undefined}
      onPointerEnter={() => setHighlighted(true)}
      onPointerLeave={() => setHighlighted(false)}
    >
      <input
        ref={inputRef}
        checked={checked}
        className={filterInputRecipe()}
        disabled={disabled}
        name={multiple ? undefined : name}
        type={multiple ? 'checkbox' : 'radio'}
        onBlur={() => setHighlighted(false)}
        onChange={onChange}
        onFocus={(event) => setHighlighted(event.currentTarget.matches(':focus-visible'))}
      />

      {multiple && (
        <span aria-hidden="true" className={multiSelectIndicatorRecipe()} data-part="indicator">
          <span className={filterTickRecipe()}>{indeterminate ? <DashIcon /> : <TickIcon />}</span>
        </span>
      )}

      <span className={multiple ? multiSelectItemLabelRecipe() : selectItemLabelRecipe()}>{label}</span>

      {!multiple && (
        <span aria-hidden="true" className={cn(selectItemCheckRecipe(), filterSingleTickRecipe())} data-part="check">
          <SelectCheckMark />
        </span>
      )}
    </label>
  );
};
